import { ApolloLink } from '@apollo/client';
import { IOperations } from '../../../interfaces/operations';
import {
  CacheMutationsRecord,
  CacheRollbackMutationsRecord,
  MutationHandler,
  MutationRollback,
} from './types';
import { parse, stringify } from 'flatted';

type Mutations = 'createOperation' | 'deleteOperation' | 'updateOperation';

// En este archivo se determina como se modifica la cache ante las diferentes mutaciones, y sus respectivos rollback en caso de error
// Por cada mutación podemos contar con múltiples campos de la cache que deben ser modificados

const handleCreateOperation: MutationHandler<{
  createOperation: IOperations;
}> = {
  operations: (previousQueryResult, { mutationResult }) => {
    const _operations = (previousQueryResult?.operations as IOperations[] | undefined) ?? [];
    const operations = [..._operations];
    const newOperation = mutationResult.data?.createOperation;
    if (!newOperation) return { operations };

    const index = operations.findIndex(
      (operation) => operation.id === newOperation.id,
    );

    // Replace at existing index or push to the end
    if (index === -1) {
      operations.push(newOperation);
    } else {
      operations.splice(index, 1, newOperation);
    }
    return { operations };
  },
};

const handleRollbackCreateOperation: MutationRollback<IOperations> = {
  operations: (queryCurrentValue, mutationCurrentValue) => {
    const operations = (queryCurrentValue?.operations as IOperations[] | undefined) ?? [];
    const id = mutationCurrentValue?.id;
    return {
      operations: operations.filter((operation) => operation.id !== id),
    };
  },
};

const handleUpdateOperation: MutationHandler<{
  updateOperation: IOperations;
}> = {
  operations: (previousQueryResult, { mutationResult }) => {
    const _operations = (previousQueryResult?.operations as IOperations[] | undefined) ?? [];
    const operations: typeof _operations = parse(stringify(_operations));
    const updatedOperation = mutationResult.data?.updateOperation;
    if (!updatedOperation) return { operations: _operations };
    const index = operations.findIndex(
      (operation) => operation.id === updatedOperation.id,
    );

    // Replace at existing index or push to the end
    if (index === -1) {
      operations.push(updatedOperation);
    } else {
      operations.splice(index, 1, updatedOperation);
    }

    return { operations };
  },
};

const handleRollbackUpdateOperation: MutationRollback<IOperations> = {
  operations: (
    queryCurrentValue,
    mutationCurrentValue,
    mutationPreviousValue,
  ) => {
    const _operations = (queryCurrentValue?.operations as IOperations[] | undefined) ?? [];
    const operations = [..._operations];
    const index = operations.findIndex(
      (operation) => operation.id === mutationCurrentValue?.id,
    );

    if (mutationPreviousValue) {
      // Replace at existing index or push to the end
      if (index === -1) {
        operations.push(mutationPreviousValue);
      } else {
        operations.splice(index, 1, mutationPreviousValue);
      }
    }

    return { operations };
  },
};

const handleDeleteOperation: MutationHandler<{
  deleteOperation: IOperations;
}> = {
  operations: (previousQueryResult, { mutationResult }) => {
    const operations = (previousQueryResult?.operations as IOperations[] | undefined) ?? [];
    const id = mutationResult.data?.deleteOperation?.id;
    return {
      operations: operations.filter((operation) => operation.id !== id),
    };
  },
};

const handleRollbackDeleteOperation: MutationRollback<IOperations> = {
  operations: (
    queryCurrentValue,
    _mutationCurrentValue,
    mutationPreviousValue,
  ) => {
    const operations = (queryCurrentValue?.operations as IOperations[] | undefined) ?? [];
    const newOperation = mutationPreviousValue;
    return newOperation
      ? { operations: [...operations, newOperation] }
      : { operations };
  },
};

const optimisticValuesCreateOperation = (
  optimisticData: { createOperation: IOperations },
  data: ApolloLink.Result<{ createOperation: IOperations }>,
) => {
  if (!optimisticData || !data) return [];

  const optimisticId = optimisticData.createOperation.id;

  const realId = data.data?.createOperation.id;

  if (!optimisticId || !realId) return [];

  return [[optimisticId, realId]] as ([string, string] | [number, number])[];
};

export const operationCacheHandlers: CacheMutationsRecord<
  Mutations,
  IOperations
> = {
  createOperation: handleCreateOperation,
  updateOperation: handleUpdateOperation,
  deleteOperation: handleDeleteOperation,
};

export const operationCacheRollbackHandlers: CacheRollbackMutationsRecord<
  Mutations,
  IOperations
> = {
  createOperation: handleRollbackCreateOperation,
  updateOperation: handleRollbackUpdateOperation,
  deleteOperation: handleRollbackDeleteOperation,
};

export const optimisticValuesToReplaceOperation: {
  [Mutation in Mutations]: (
    optimisticData: { [key in Mutation]: IOperations },
    data: ApolloLink.Result<{ [key in Mutation]: IOperations }>,
  ) => ([string, string] | [number, number])[];
} = {
  createOperation: optimisticValuesCreateOperation,
  deleteOperation: () => [],
  updateOperation: () => [],
};
